'use client';

import { ChevronDown, ChevronUp, CheckCircle, Lightbulb } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { forwardRef } from 'react';
import { CriterionTag } from '@/components/audit/CriterionTag';
import styles from '@/components/audit/ProReportResults.module.css';
import { SeverityMark } from '@/components/audit/SeverityMark';
import { normalizeSeverity, getFindingEffort, deriveCategory } from '@/lib/audit/pro-report-utils';
import type { Finding } from './types';

interface FindingCardProps {
  finding: Finding;
  isExpanded: boolean;
  onToggle: () => void;
}

export const FindingCard = forwardRef<HTMLElement, FindingCardProps>(function FindingCard(
  { finding, isExpanded, onToggle },
  ref
) {
  const t = useTranslations('proReport.findingCard');
  const severity = normalizeSeverity(finding.severity);
  const category = deriveCategory(finding);
  const effort = getFindingEffort(finding);
  const impactKey = `impact${severity.charAt(0).toUpperCase() + severity.slice(1)}` as const;
  const effortKey = effort.bars <= 1 ? 'effortQuick' : effort.bars === 2 ? 'effortMedium' : 'effortComplex';

  return (
    <article
      ref={ref}
      className={`${styles.findingCard} ${styles[`severity${severity.charAt(0).toUpperCase() + severity.slice(1)}`]}`}
    >
      {/* Card Header */}
      <div className={styles.findingHeader}>
        <div className={styles.findingBadges}>
          <SeverityMark severity={finding.severity} />
          <span className={styles.categoryBadge}>{finding.auditCategoryLabel || category}</span>
          <CriterionTag code={finding.auditCode} categoryLabel={finding.auditCategoryLabel} />
        </div>
        <button
          className={styles.expandButton}
          onClick={onToggle}
          aria-label={isExpanded ? t('collapse') : t('expand')}
        >
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {/* Card Title */}
      <h3 className={styles.findingTitle}>
        {finding.title || finding.principleTitle || finding.help || t('issueDetected')}
      </h3>

      {/* Description - always visible */}
      <p className={styles.findingDescription}>{finding.description}</p>

      {/* Expanded Content */}
      {isExpanded && (
        <div className={styles.findingDetails}>
          {/* Principle Badge */}
          {finding.principle && (
            <div className={styles.principleSection}>
              <span className={styles.detailLabel}>{t('basedOn')}</span>
              <div className={styles.principleBadge}>
                <Lightbulb size={14} />
                {finding.principle}
              </div>
            </div>
          )}

          {/* Affected Elements */}
          {finding.affectedElements && finding.affectedElements.length > 0 && (
            <div className={styles.affectedSection}>
              <span className={styles.detailLabel}>{t('affected')}</span>
              <ul className={styles.elementList}>
                {finding.affectedElements.slice(0, 5).map((el, i) => (
                  <li key={i}>
                    <code>{el}</code>
                  </li>
                ))}
                {finding.affectedElements.length > 5 && (
                  <li className={styles.moreElements}>
                    {t('more', { count: finding.affectedElements.length - 5 })}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Recommendation */}
          {finding.recommendation && (
            <div className={styles.recommendationSection}>
              <span className={styles.detailLabel}>{t('recommendation')}</span>
              <div className={styles.recommendationBox}>
                <CheckCircle size={16} className={styles.recommendationIcon} />
                <p>{finding.recommendation}</p>
              </div>
            </div>
          )}

          {/* Meta Info */}
          <div className={styles.findingMeta}>
            <div className={styles.metaBlock}>
              <span className={styles.metaLabel}>{t('impact')}</span>
              <span className={styles.metaValue}>{t(impactKey)}</span>
            </div>
            <div className={styles.metaBlock}>
              <span className={styles.metaLabel}>{t('effort')}</span>
              <div className={styles.effortBar}>
                {[1, 2, 3].map((bar) => (
                  <span
                    key={bar}
                    className={`${styles.effortDot} ${bar <= effort.bars ? styles.effortFilled : ''}`}
                  />
                ))}
                <span className={styles.effortLabel}>{t(effortKey)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
});
